/**
 * 认证相关API
 */

import type { AxiosRequestConfig } from 'axios'
import { request } from '@/shared/api/request'
import type { BatchRequestConfig } from '@/shared/api/use-api-batch'

type RequestConfig = AxiosRequestConfig | BatchRequestConfig

// 登录请求参数
export interface LoginRequest {
  username: string
  password: string
}

// 用户信息（permissions 由登录/me 接口合并写入，供前端权限管理器使用）
export interface UserInfo {
  id: number
  username: string
  email: string
  name: string
  name_remark?: string
  nationality?: string
  language?: string
  language_micro?: string
  school?: string
  department?: string
  phone?: string
  user_type: 'normal' | 'restricted' | 'admin'
  status: 'active' | 'inactive' | 'suspended'
  is_active: boolean
  group_id?: number
  last_login_at?: string
  created_at: string
  updated_at: string
  /** 接口下发的菜单权限 code -> boolean，合并后供 permissionManager 使用（兼容） */
  permissions?: Record<string, boolean>
  /** 与 DB permission_code 一致的权限 code 列表，优先用于菜单与路由 */
  permission_codes?: string[]
  /** 角色 code 列表；含 admin 时前端全开 */
  role_codes?: string[]
  /** 角色摘要，头像下拉展示 name */
  roles?: Array<{ id?: number; code: string; name?: string }>
}

// 登录响应
export interface LoginResponse {
  access_token: string
  token_type: string
  user: UserInfo
  permissions: Record<string, boolean>
  /** 与 DB permission_code 一致，用于前端严格按权限展示菜单 */
  permission_codes?: string[]
  role_codes?: string[]
}

// 用户权限响应
export interface UserPermissionsResponse {
  permissions: Record<string, boolean>
  permission_codes?: string[]
  role_codes?: string[]
}

// 更新用户资料请求
export interface UpdateProfileRequest {
  name?: string
  email?: string
  phone?: string
  language?: string
  language_micro?: string
  nationality?: string
  school?: string
  department?: string
  name_remark?: string
}

// 修改密码请求
export interface ChangePasswordRequest {
  oldPassword: string
  newPassword: string
}

/**
 * 用户登录
 */
export const login = (data: LoginRequest): Promise<LoginResponse> => {
  return request.post('/auth/login', data)
}

/**
 * 获取当前用户信息
 */
export const getCurrentUser = (): Promise<LoginResponse> => {
  return request.get('/auth/me')
}

/**
 * 获取用户权限
 */
export const getUserPermissions = (
  config?: RequestConfig,
): Promise<UserPermissionsResponse> => {
  return request.get('/auth/permissions', config)
}

/**
 * 用户登出
 */
export const logout = (): Promise<{ message: string }> => {
  return request.post('/auth/logout')
}

/**
 * 刷新令牌
 */
export const refreshToken = (): Promise<LoginResponse> => {
  return request.post('/auth/refresh-token')
}

/**
 * 更新用户资料
 */
export const updateProfile = (data: UpdateProfileRequest): Promise<UserInfo> => {
  return request.put('/auth/profile', data)
}

/**
 * 修改密码
 */
export const changePassword = (data: ChangePasswordRequest): Promise<{ message: string }> => {
  return request.post('/auth/change-password', data)
}

// 导出API对象（兼容旧代码）
export const authApi = {
  login,
  getCurrentUser,
  getUserPermissions,
  logout,
  refreshToken,
  updateProfile,
  changePassword
}

